<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import { usePageStore } from "../stores/pageStore";

const props = withDefaults(
  defineProps<{
    /** Lebar panel kiri dalam persen (v-model) */
    modelValue?: number;
    /** Batas minimum lebar panel kiri (%) */
    min?: number;
    /** Batas maksimum lebar panel kiri (%) */
    max?: number;
    /** Lebar handle dalam px */
    handleSize?: number;
    /** Nonaktifkan resize */
    disabled?: boolean;
    /** Class tambahan untuk masing-masing panel */
    leftClass?: string;
    rightClass?: string;
    /**
     * Simpan lebar panel ke localStorage (default true).
     * Key otomatis = user + halaman (route); bisa di-override lewat `storageKey`.
     */
    persist?: boolean;
    /** Override key persistensi (tanpa prefix). Kalau diisi, `persist` dianggap true. */
    storageKey?: string;
  }>(),
  {
    modelValue: 33.33,
    min: 20,
    max: 80,
    handleSize: 4,
    disabled: false,
    leftClass: "",
    rightClass: "",
    persist: true,
    storageKey: "",
  },
);

// Pinia mungkin tidak aktif kalau widget ini dipakai di luar app utama; jangan sampai crash.
let pageStore: ReturnType<typeof usePageStore> | null = null;
try {
  pageStore = usePageStore();
} catch {
  pageStore = null;
}

const STORAGE_PREFIX = "astrox:split-pane:";

function resolveStorageKey(): string | null {
  if (!props.persist && !props.storageKey) return null;

  // Override manual: samakan untuk semua user/halaman (mis. <SplitPaneWidget storage-key="foo" />)
  if (props.storageKey) return `${STORAGE_PREFIX}${props.storageKey}`;

  const userKey = pageStore?.userInfo?.email ?? pageStore?.userInfo?.name ?? "anonymous";
  const pageKey = (pageStore?.route?.name as string) ?? pageStore?.route?.path ?? "unknown";
  return `${STORAGE_PREFIX}${userKey}:${pageKey}`;
}

function readStoredWidth(): number | null {
  if (typeof localStorage === "undefined") return null;
  const key = resolveStorageKey();
  if (!key) return null;

  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

function writeStoredWidth() {
  if (typeof localStorage === "undefined") return;
  const key = resolveStorageKey();
  if (!key) return;

  try {
    localStorage.setItem(key, String(leftWidth.value));
  } catch {
    // Storage penuh / diblokir — abaikan, ini cuma preferensi UI.
  }
}

let saveTimer: ReturnType<typeof setTimeout> | undefined;

// Tulis setelah resize berhenti (di-debounce) supaya tidak menulis tiap pointermove.
function scheduleSave() {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    saveTimer = undefined;
    writeStoredWidth();
  }, 250);
}

const emit = defineEmits<{
  (e: "update:modelValue", value: number): void;
  (e: "resize-start"): void;
  (e: "resize-end", value: number): void;
}>();

const containerRef = ref<HTMLElement | null>(null);
const leftWidth = ref(clamp(readStoredWidth() ?? props.modelValue));
const isResizing = ref(false);

function clamp(value: number) {
  return Math.min(props.max, Math.max(props.min, value));
}

// Kalau halaman berubah (key persistensi berganti), muat lebar tersimpan halaman itu.
watch(
  () => resolveStorageKey(),
  () => {
    const stored = readStoredWidth();
    if (stored !== null) leftWidth.value = clamp(stored);
  },
);

onMounted(() => {
  // pageStore.route bisa baru terisi setelah mount pada beberapa kasus; pastikan tetap ter-load.
  const stored = readStoredWidth();
  if (stored !== null) leftWidth.value = clamp(stored);
});

// Sinkronisasi jika v-model diubah dari luar
watch(
  () => props.modelValue,
  (val) => {
    if (!isResizing.value) leftWidth.value = clamp(val);
  },
);

function setWidth(value: number) {
  leftWidth.value = clamp(value);
  emit("update:modelValue", leftWidth.value);
  scheduleSave();
}

function onPointerMove(e: PointerEvent) {
  if (!isResizing.value || !containerRef.value) return;
  const rect = containerRef.value.getBoundingClientRect();
  if (rect.width === 0) return;
  setWidth(((e.clientX - rect.left) / rect.width) * 100);
}

function stopResize() {
  if (!isResizing.value) return;
  isResizing.value = false;
  window.removeEventListener("pointermove", onPointerMove);
  window.removeEventListener("pointerup", stopResize);
  window.removeEventListener("pointercancel", stopResize);
  document.body.style.cursor = "";
  emit("resize-end", leftWidth.value);

  // Simpan langsung saat resize selesai.
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = undefined;
  }
  writeStoredWidth();
}

function startResize(e: PointerEvent) {
  if (props.disabled) return;
  e.preventDefault();
  isResizing.value = true;
  window.addEventListener("pointermove", onPointerMove);
  window.addEventListener("pointerup", stopResize);
  window.addEventListener("pointercancel", stopResize);
  document.body.style.cursor = "col-resize";
  emit("resize-start");
}

// Aksesibilitas: geser dengan keyboard
function onKeydown(e: KeyboardEvent) {
  if (props.disabled) return;
  const step = e.shiftKey ? 10 : 2;
  if (e.key === "ArrowLeft") {
    e.preventDefault();
    setWidth(leftWidth.value - step);
  } else if (e.key === "ArrowRight") {
    e.preventDefault();
    setWidth(leftWidth.value + step);
  }
}

onBeforeUnmount(() => {
  stopResize();
  // Flush kalau ada save yang masih menunggu (mis. dari keyboard/dblclick).
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = undefined;
    writeStoredWidth();
  }
});
</script>

<template>
  <div ref="containerRef" class="grid relative min-h-0" :class="{ 'select-none': isResizing }" :style="{ gridTemplateColumns: `${leftWidth}% ${handleSize}px 1fr` }">
    <!-- Panel kiri: tidak boleh scroll di level panel; scroll diatur oleh konten di dalam (max-height + overflow-y-auto) -->
    <div class="h-full min-h-0 overflow-hidden" :class="leftClass">
      <slot name="left" />
    </div>

    <!-- Handle -->
    <div
      role="separator"
      aria-orientation="vertical"
      :aria-valuenow="Math.round(leftWidth)"
      :aria-valuemin="min"
      :aria-valuemax="max"
      :tabindex="disabled ? -1 : 0"
      class="relative z-10 flex h-full items-center justify-center transition-colors group touch-none"
      :class="[isResizing ? 'bg-blue-500' : 'bg-gray-200', disabled ? 'cursor-default' : 'cursor-col-resize hover:bg-blue-500']"
      @pointerdown="startResize"
      @keydown="onKeydown"
      @dblclick="!disabled && setWidth(33.33)"
    >
      <div class="h-8 w-0.5 rounded-full transition-colors" :class="isResizing ? 'bg-white' : 'bg-gray-400 group-hover:bg-white'"></div>
    </div>

    <!-- Panel kanan: tidak boleh scroll di level panel; scroll diatur oleh konten di dalam (max-height + overflow-y-auto) -->
    <div class="h-full min-h-0 overflow-hidden border-l" :class="rightClass">
      <slot name="right" />
    </div>
  </div>
</template>
